import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Flower2, Camera, Upload, X, CheckCircle2, ShieldCheck, Sparkles } from "lucide-react";
import DahliasShell from "../components/dahlias/DahliasShell";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTOS = 6;

const NZ_REGIONS = [
  "Northland", "Auckland", "Waikato", "Bay of Plenty", "Gisborne",
  "Hawke's Bay", "Taranaki", "Manawatu, Whanganui", "Wellington",
  "Hutt Valley", "Wairarapa", "Tasman, Nelson", "Marlborough",
  "West Coast", "Canterbury", "Otago", "Southland",
  "Other / outside Aotearoa",
];

export default function BreedersGarden() {
  useDocumentTitle(
    "Heritage Garden, Breeders' Bed · Submit your seedling",
    "Submit your beautiful seedlings and new breeds to the Heritage Dahlias summer garden bed. Approved tubers receive a NZ Post courier ticket and a credit toward Heritage tubers and Bloom Boxes."
  );

  const [form, setForm] = useState({
    breeder_name: "",
    breeder_email: "",
    breeder_phone: "",
    breeder_region: "",
    breeder_postal_address: "",
    breeder_bio: "",
    dahlia_name: "",
    parentage: "",
    description: "",
    photos: [],
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = MAX_PHOTOS - form.photos.length;
    const toUpload = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.message(`Only ${remaining} more photo${remaining === 1 ? "" : "s"} allowed.`);
    }
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const results = await Promise.allSettled(
      toUpload.map((file) => {
        const fd = new FormData();
        fd.append("file", file);
        return axios.post(`${API}/seedlings/photo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      })
    );
    const urls = [];
    for (const r of results) {
      if (r.status === "fulfilled" && r.value.data?.url) {
        urls.push(`${base}${r.value.data.url}`);
      } else if (r.status === "rejected") {
        toast.error(r.reason?.response?.data?.detail || "Couldn't upload one of your photos.");
      }
    }
    if (urls.length) setForm((f) => ({ ...f, photos: [...f.photos, ...urls].slice(0, MAX_PHOTOS) }));
    e.target.value = "";
  };

  const removePhoto = (i) =>
    setForm((f) => ({ ...f, photos: f.photos.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ["breeder_name", "breeder_email", "breeder_postal_address", "dahlia_name", "description"];
    for (const k of required) {
      if (!String(form[k] || "").trim()) {
        toast.error("Please fill in all the required fields.");
        return;
      }
    }
    if (form.photos.length === 0) {
      toast.error("Please attach at least one photo of your seedling.");
      return;
    }
    try {
      setBusy(true);
      await axios.post(`${API}/seedlings/submit`, form);
      toast.success("Submission received. Check your inbox for confirmation.");
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send your submission just now.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <DahliasShell>
        <section className="max-w-3xl mx-auto px-5 md:px-8 py-20 md:py-28 text-center">
          <CheckCircle2 className="w-14 h-14 mx-auto text-[var(--berry)]" aria-hidden="true" />
          <h1 className="mt-6 font-display text-4xl md:text-5xl text-[var(--dahlias-ink)]">
            Thank you, your seedling is in our queue.
          </h1>
          <p className="mt-5 text-base md:text-lg text-[var(--dahlias-ink)]/85 leading-relaxed">
            We&rsquo;ll assess every submission carefully. If your seedling is a fit for this
            summer&rsquo;s Heritage Garden bed, we&rsquo;ll be in touch with a NZ Post courier
            ticket and the credit we&rsquo;d love to apply to your account.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <Link
              to="/dahlias"
              data-testid="breeders-done-back"
              className="inline-flex items-center gap-2 px-6 h-12 bg-[var(--dahlias-ink)] hover:bg-[var(--berry-deep)] text-[#FBE6D8] font-display text-sm tracking-[0.14em] uppercase rounded-full transition-colors"
            >
              Back to Heritage Dahlias
            </Link>
          </div>
        </section>
      </DahliasShell>
    );
  }

  return (
    <DahliasShell>
      <ApprovedBloomsShowcase />

      {/* Hero */}
      <section id="breeders-apply" className="relative border-b-2 border-[var(--berry)]/20" style={{ backgroundColor: "#FAF5EB" }}>
        <div className="relative max-w-4xl mx-auto px-5 md:px-8 pt-14 md:pt-20 pb-12 md:pb-16 text-center">
          <span
            data-testid="breeders-eyebrow"
            className="inline-flex items-center gap-3 px-4 py-1.5 border border-[var(--berry)]/50 rounded-full font-display tracking-[0.4em] text-xs text-[var(--berry)] uppercase"
            style={{ backgroundColor: "rgba(250, 245, 235, 0.85)" }}
          >
            <span aria-hidden="true">✦</span>
            Heritage Garden Breeders&rsquo; Bed
            <span aria-hidden="true">✦</span>
          </span>
          <h1
            data-testid="breeders-heading"
            className="font-display text-4xl md:text-6xl text-[var(--dahlias-ink)] mt-6 leading-[1.05]"
          >
            Submit your <span className="italic text-[var(--berry)]">summer seedling.</span>
          </h1>
          <p className="mt-7 text-base md:text-lg text-[var(--dahlias-ink)]/85 max-w-2xl mx-auto leading-relaxed">
            Each summer the Heritage Garden plays host to new dahlia seedlings and breeder-bred
            varieties from across Aotearoa. If you&rsquo;ve grown something beautiful, we&rsquo;d
            love to put it in our bed, photograph it through the season and credit your name to it.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-20">
        <h2 className="text-center font-display text-3xl md:text-4xl text-[var(--dahlias-ink)] leading-[1.1]">
          How it works
        </h2>
        <ol className="mt-10 grid md:grid-cols-4 gap-5">
          {[
            { n: 1, t: "Submit", d: "Tell us about your seedling and upload a few photos." },
            { n: 2, t: "We assess", d: "Our team reviews every submission with care." },
            { n: 3, t: "Courier ticket", d: "Approved breeders receive a NZ Post courier ticket from us." },
            { n: 4, t: "Garden + credit", d: "Your bloom is planted, displayed with your name, and you receive credit toward Heritage tubers and boxes." },
          ].map((s) => (
            <li
              key={s.n}
              className="border-2 border-[var(--berry)]/25 p-5 rounded-2xl"
              style={{ backgroundColor: "rgba(250, 245, 235, 0.9)" }}
            >
              <div className="w-9 h-9 rounded-full bg-[var(--berry)] text-[#FBE6D8] flex items-center justify-center font-display">
                {s.n}
              </div>
              <h3 className="mt-3 font-display text-lg text-[var(--dahlias-ink)]">{s.t}</h3>
              <p className="mt-1.5 text-sm text-[var(--dahlias-ink)]/80 leading-relaxed">{s.d}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Form */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 pb-20 md:pb-28">
        <div className="text-center mb-10">
          <span className="font-display tracking-[0.45em] text-[10px] text-[var(--berry)] uppercase">
            Tell us about your seedling
          </span>
          <h2 className="mt-3 font-display text-3xl md:text-4xl text-[var(--dahlias-ink)] leading-[1.05]">
            Your <span className="italic text-[var(--berry)]">dahlia, your story.</span>
          </h2>
        </div>

        <form
          onSubmit={submit}
          data-testid="breeders-form"
          className="border-2 border-[var(--berry)]/25 p-6 md:p-9 space-y-7 rounded-2xl shadow-[0_10px_28px_rgba(110,30,58,0.12)]"
          style={{ backgroundColor: "rgba(250, 245, 235, 0.95)" }}
        >
          <Section title="About you">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Your name *">
                <Input data-testid="breeders-name" value={form.breeder_name} onChange={(e) => setForm({ ...form, breeder_name: e.target.value })} className="h-12" placeholder="Full name" />
              </Field>
              <Field label="Email *">
                <Input type="email" data-testid="breeders-email" value={form.breeder_email} onChange={(e) => setForm({ ...form, breeder_email: e.target.value })} className="h-12" placeholder="you@example.co.nz" />
              </Field>
              <Field label="Phone (optional)">
                <Input data-testid="breeders-phone" value={form.breeder_phone} onChange={(e) => setForm({ ...form, breeder_phone: e.target.value })} className="h-12" placeholder="021 ..." />
              </Field>
              <Field label="Region">
                <select
                  data-testid="breeders-region"
                  value={form.breeder_region}
                  onChange={(e) => setForm({ ...form, breeder_region: e.target.value })}
                  className="h-12 w-full px-4 rounded-md border border-input bg-background text-sm"
                >
                  <option value="">Where do you grow?</option>
                  {NZ_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </Field>
              <Field label="Postal address *" wide>
                <Textarea data-testid="breeders-address" value={form.breeder_postal_address} onChange={(e) => setForm({ ...form, breeder_postal_address: e.target.value })} rows={2} placeholder="So we can send your NZ Post courier ticket" />
              </Field>
              <Field label="A little about you as a breeder (optional)" wide>
                <Textarea data-testid="breeders-bio" value={form.breeder_bio} onChange={(e) => setForm({ ...form, breeder_bio: e.target.value })} rows={3} placeholder="How long you've been growing, what you love, anything you'd like us to share alongside your bloom." />
              </Field>
            </div>
          </Section>

          <Section title="About the seedling">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Proposed dahlia name *">
                <Input data-testid="breeders-dahlia" value={form.dahlia_name} onChange={(e) => setForm({ ...form, dahlia_name: e.target.value })} className="h-12" placeholder="e.g. Hazel's Hope" />
              </Field>
              <Field label="Parentage (if known)">
                <Input data-testid="breeders-parentage" value={form.parentage} onChange={(e) => setForm({ ...form, parentage: e.target.value })} className="h-12" placeholder="e.g. Cafe au Lait × Lakeview Sunburst" />
              </Field>
              <Field label="Describe the bloom *" wide>
                <Textarea data-testid="breeders-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={5} placeholder="Form, colour, size, height, anything that makes it special." />
              </Field>
            </div>
          </Section>

          <Section title="Photos *">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {form.photos.map((url, i) => (
                <div key={i} className="relative aspect-square border-2 border-[var(--berry)]/30 rounded-lg overflow-hidden bg-[#FBE6D8]">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removePhoto(i)} className="absolute top-1 right-1 w-7 h-7 rounded-full bg-[var(--dahlias-ink)] text-[#FBE6D8] flex items-center justify-center hover:bg-[var(--berry)] transition-colors" aria-label="Remove photo">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {form.photos.length < MAX_PHOTOS && (
                <label className="aspect-square border-2 border-dashed border-[var(--berry)]/40 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[var(--berry)] hover:bg-[#FBE6D8]/30 transition-colors">
                  <Camera className="w-6 h-6 text-[var(--berry)]" />
                  <span className="mt-1 text-[10px] tracking-[0.18em] uppercase text-[var(--berry)]">Add</span>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={onPhotos}
                    className="hidden"
                    data-testid="breeders-photos-input"
                  />
                </label>
              )}
            </div>
            <p className="mt-3 text-xs text-[var(--dahlias-ink)]/65">
              Up to 6 photos. We resize and optimise them automatically.
            </p>
          </Section>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2 border-t border-[var(--berry)]/15">
            <p className="text-xs text-[var(--dahlias-ink)]/65 max-w-sm leading-relaxed inline-flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 mt-0.5 text-[var(--berry)] shrink-0" aria-hidden="true" />
              Your details are used only to assess your submission and post your courier ticket.
              See our{" "}
              <a href="/legal/privacy" target="_blank" rel="noreferrer" className="underline text-[var(--berry)]">
                Privacy Policy
              </a>.
            </p>
            <Button
              type="submit"
              disabled={busy}
              data-testid="breeders-submit"
              className="h-13 px-8 bg-[var(--dahlias-ink)] hover:bg-[var(--berry-deep)] text-[#FBE6D8] rounded-full font-display tracking-[0.12em] uppercase inline-flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              {busy ? "Sending…" : "Submit my seedling"}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </form>
      </section>
    </DahliasShell>
  );
}

function Section({ title, children }) {
  return (
    <fieldset className="space-y-4">
      <legend className="font-display tracking-[0.3em] text-[10px] uppercase text-[var(--berry)] mb-1">
        {title}
      </legend>
      {children}
    </fieldset>
  );
}

function Field({ label, children, wide }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <Label className="font-display tracking-[0.3em] text-[10px] uppercase text-[var(--berry)]">
        {label}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Approved Blooms showcase , the new landing surface on /breeders-garden.
   Pulls /api/seedlings/garden, falls back to a graceful empty state, then
   nudges visitors to the Submit Your Bloom CTA which smooth-scrolls to the
   existing application form below.
   ───────────────────────────────────────────────────────────────────────── */
function ApprovedBloomsShowcase() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    axios.get(`${API}/seedlings/garden`)
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRows([]));
  }, []);

  const scrollToApply = (e) => {
    e.preventDefault();
    document.getElementById("breeders-apply")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      data-testid="breeders-approved-blooms"
      className="border-b-2 border-[var(--berry)]/20"
      style={{ backgroundColor: "#FAF5EB" }}
    >
      <div className="max-w-6xl mx-auto px-5 md:px-8 pt-14 md:pt-20 pb-12 md:pb-16">
        <header className="text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 border border-[var(--berry)]/50 rounded-full font-display tracking-[0.35em] text-[11px] text-[var(--berry)] uppercase">
            <Sparkles className="w-3 h-3" aria-hidden="true" /> Approved Blooms <Sparkles className="w-3 h-3" aria-hidden="true" />
          </span>
          <h1 className="font-display text-4xl md:text-6xl text-[var(--dahlias-ink)] mt-5 leading-[1.05]">
            Heritage Garden <span className="italic text-[var(--berry)]">Breeders&rsquo; Bed.</span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-[var(--dahlias-ink)]/85 max-w-2xl mx-auto leading-relaxed">
            Meet the heritage and new-season seedlings growing in our garden this season , credited to the breeders who raised them.
          </p>
        </header>

        {rows === null ? (
          <div className="mt-10 text-center text-sm text-[var(--dahlias-ink)]/60">Loading approved blooms…</div>
        ) : rows.length === 0 ? (
          <div className="mt-10 border-2 border-dashed border-[var(--berry)]/30 p-8 text-center" data-testid="breeders-approved-empty">
            <Flower2 className="w-10 h-10 mx-auto text-[var(--berry)]/60" />
            <p className="mt-4 font-display text-lg text-[var(--dahlias-ink)]">No approved blooms yet, the bed is still being prepared.</p>
            <p className="mt-2 text-sm text-[var(--dahlias-ink)]/75">Your seedling could be the first published here.</p>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="breeders-approved-grid">
            {rows.map((b) => {
              const photo = Array.isArray(b.photos) && b.photos[0] ? b.photos[0] : null;
              return (
                <article
                  key={b.id}
                  className="border-2 border-[var(--berry)]/25 rounded-2xl overflow-hidden bg-white shadow-[0_8px_20px_rgba(110,30,58,0.10)]"
                  data-testid={`breeders-bloom-${b.id}`}
                >
                  <div className="aspect-[4/3] bg-[var(--dahlias-cream)] relative">
                    {photo ? (
                      <img src={photo} alt={b.dahlia_name || "Approved bloom"} loading="lazy" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full grid place-items-center"><Flower2 className="w-12 h-12 text-[var(--berry)]/40" /></div>
                    )}
                    <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 bg-[var(--berry)] text-[#FBE6D8] text-[10px] tracking-[0.25em] uppercase rounded">
                      <CheckCircle2 className="w-3 h-3" /> Approved
                    </span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-xl text-[var(--dahlias-ink)] leading-tight">{b.dahlia_name || "Unnamed bloom"}</h3>
                    <div className="mt-1 text-[11px] tracking-[0.3em] uppercase text-[var(--berry)]">
                      {b.breeder_name}{b.breeder_region ? ` · ${b.breeder_region}` : ""}
                    </div>
                    {b.description && (
                      <p className="mt-3 text-sm text-[var(--dahlias-ink)]/80 leading-relaxed line-clamp-3">{b.description}</p>
                    )}
                    {b.parentage && (
                      <p className="mt-2 text-[11px] italic text-[var(--dahlias-ink)]/60">Parentage: {b.parentage}</p>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {/* Submit Your Bloom CTA */}
        <div className="mt-12 md:mt-16 text-center">
          <a
            href="#breeders-apply"
            onClick={scrollToApply}
            data-testid="breeders-submit-cta"
            className="inline-flex items-center gap-2 px-7 py-3 bg-[var(--berry)] hover:bg-[var(--berry-bright)] text-[#FBE6D8] rounded-full font-display tracking-[0.25em] text-sm uppercase transition shadow-[0_8px_20px_rgba(110,30,58,0.25)]"
          >
            <Flower2 className="w-4 h-4" /> Submit Your Bloom <ArrowRight className="w-4 h-4" />
          </a>
          <p className="mt-3 text-xs text-[var(--dahlias-ink)]/65 italic">
            Approved breeders receive a NZ Post courier ticket + a credit toward Heritage tubers.
          </p>
        </div>
      </div>
    </section>
  );
}

